import classNames from "classnames";
import React, { useEffect, useState } from "react";
import { Svg, SVG_ICONS } from "../Svg/Svg";
import { Typography } from "../Typography/Typography";

import "./Accordion.scss";
import { useTranslation } from "react-i18next";

/**
 * Interface for Accordion sub-components.
 *
 * @description Defines the sub-components available on the Accordion component.
 */
interface AccordionSubComponents {
  Item: React.FC<AccordionItemProps>;
}

/**
 * Props for the Accordion container component.
 *
 * @description The Accordion component is a container for Accordion.Item components,
 * providing a consistent layout for collapsible content sections.
 *
 * @property {React.ReactNode} [children] - Accordion.Item components to render inside.
 *
 * @example
 * <Accordion>
 *   <Accordion.Item id="1" title="Section 1">Content 1</Accordion.Item>
 *   <Accordion.Item id="2" title="Section 2">Content 2</Accordion.Item>
 * </Accordion>
 */
interface Props {
  children?: React.ReactNode;
}

/**
 * A container component for collapsible accordion items.
 *
 * @description Accordion provides a container for organizing Accordion.Item components.
 * Each item can be expanded or collapsed independently.
 *
 * @param {Props} props - The component props
 * @returns {JSX.Element} A div container with accordion styling
 *
 * @see Props for all available properties
 * @see Item for individual accordion items
 */
export const Accordion: React.FC<Props> & AccordionSubComponents = (props) => {
  const { children } = props;
  return <div className="accordion">{children}</div>;
};

/**
 * Props for the Accordion Item component.
 *
 * @description The Item component is a single collapsible section within an Accordion.
 *
 * @property {string | number} id - Unique identifier for the accordion item. Required.
 * @property {string} title - The title text displayed in the accordion header. Required.
 * @property {React.ReactNode} [children] - Content to display when the item is expanded.
 * @property {string} [activeItem] - The title of the currently active (expanded) item.
 * @property {(isOpen: boolean) => void} [onClick] - Callback when the item is clicked.
 *   Receives a boolean indicating whether the item is now open.
 * @property {string} [testId] - Data-testid attribute for testing purposes.
 * @property {string} [className] - Additional CSS class names.
 *
 * @example
 * <Accordion.Item
 *   id="faq-1"
 *   title="What is this service?"
 *   activeItem={activeItem}
 *   onClick={(isOpen) => console.log('Item is open:', isOpen)}
 * >
 *   <p>This is the answer to the question.</p>
 * </Accordion.Item>
 */
interface AccordionItemProps {
  id: string | number;
  title: string;
  children?: React.ReactNode;
  activeItem?: string;
  onClick?: (isOpen: boolean) => void;
  testId?: string;
  className?: string;
}

/**
 * A single collapsible item within an Accordion.
 *
 * @description Item renders a collapsible section with a title header and expandable content.
 * When clicked, the item toggles between expanded and collapsed states. The chevron icon
 * indicates the current state.
 *
 * Features:
 * - Expandable/collapsible content
 * - Active state management via activeItem prop
 * - Typography integration for consistent text styling
 * - Chevron icon for visual feedback
 *
 * @param {AccordionItemProps} props - The component props
 * @returns {JSX.Element} A collapsible accordion item
 *
 * @see AccordionItemProps for all available properties
 */
const Item: React.FC<AccordionItemProps> = (props) => {
  const { t } = useTranslation();
  const { title, children, onClick, activeItem, id, testId } = props;
  const [isActive, setIsActive] = useState<boolean>(title === activeItem);
  const wrapClass = classNames("accordion-item", {
    active: title === activeItem,
  });

  useEffect(
    () => {
      setIsActive(title === activeItem);
    },
     
    [activeItem]
  );

  return (
    <div className={wrapClass} id={String(id)}>
      <button
        data-testid={testId}
        className="accordion-item__title"
        onClick={() => {
          setIsActive(!isActive);
          if (onClick) {
            onClick(!isActive);
          }
        }}
      >
        <Typography
          variant="body2"
          weight="wBold"
          className="accordion-item__title__text"
        >
          {title}
        </Typography>
        <Svg
          icon={SVG_ICONS.CHEVRON_MORE}
          width={14}
          height={14}
          title={t("PRIVACY_COMMON_EXPAND_COLLAPSE_ALT_TEXT")}
        />
      </button>
      <div className="accordion-item__content">{children}</div>
    </div>
  );
};

Accordion.Item = Item;

export default Accordion;
